import { Shield, Check, Minus, ArrowLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import { SECTIONS, type NavItem } from '../lib/admin-nav'
import { ALL, type AdminRole } from '../lib/admin-roles'

// Read-only matrix of which admin role can access which page.
// Source: the shared SECTIONS array in `lib/admin-nav.ts` — same data
// the sidebar reads. Editing a role's access lives in code (the
// SECTIONS array and the matching server-side `requireRole(...)`
// middleware), not in the admin UI; this page is a visualisation of
// the current state so super_admin doesn't have to read source.

const ROLES: { id: AdminRole; label: string }[] = [
  { id: 'super_admin', label: 'Super Admin' },
  { id: 'operations',  label: 'Operations' },
  { id: 'accounting',  label: 'Accounting' },
  { id: 'marketing',   label: 'Marketing' },
]

function canRoleSee(item: NavItem, role: AdminRole): boolean {
  if (item.superAdminOnly) return role === 'super_admin'
  if (!item.roles) return true            // undefined = visible to all
  return item.roles.includes(role)
}

export default function PermissionsMatrix() {
  return (
    <div className="p-6 max-w-6xl">
      <Link
        to="/admin-users"
        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-white mb-3 transition-colors"
      >
        <ArrowLeft size={13} /> Back to Admin Users
      </Link>
      <div className="flex items-center gap-3 mb-2">
        <Shield className="text-[#C41E3A]" size={22} />
        <h1 className="text-lg font-semibold text-white">Permissions matrix</h1>
      </div>
      <p className="text-xs text-gray-500 mb-6 leading-relaxed">
        Which admin role can access which page. Read-only — to change
        role access, edit <code className="text-[#C41E3A] bg-white/5 px-1 rounded">admin/src/lib/admin-nav.ts</code> and the
        matching server-side <code className="text-[#C41E3A] bg-white/5 px-1 rounded">requireRole(...)</code> calls together,
        then redeploy. The full access matrix doc lives at <code className="text-gray-400 bg-white/5 px-1 rounded">docs/admin/admin-users.md</code>.
      </p>

      <div className="space-y-6">
        {SECTIONS.map(section => (
          <section key={section.id} className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 bg-white/[0.02] border-b border-[#2e2e2e]">
              <h2 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                {section.label}
              </h2>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2e2e2e]">
                  <th className="text-left px-4 py-2.5 font-medium text-gray-400 w-[40%]">Page</th>
                  {ROLES.map(r => (
                    <th key={r.id} className="text-center px-2 py-2.5 font-medium text-gray-400">
                      {r.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.items.map(item => (
                  <tr key={item.to} className="border-b border-[#2e2e2e] last:border-b-0">
                    <td className="px-4 py-2.5 text-white">
                      <div className="flex items-center gap-2.5">
                        <item.icon size={14} />
                        <span>{item.label}</span>
                        <code className="text-[10px] text-gray-500 ml-1">{item.to}</code>
                      </div>
                    </td>
                    {ROLES.map(r => {
                      const allowed = canRoleSee(item, r.id)
                      return (
                        <td key={r.id} className="text-center px-2 py-2.5">
                          {allowed ? (
                            <Check size={15} className="inline text-emerald-500" />
                          ) : (
                            <Minus size={15} className="inline text-gray-700" />
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>

      <p className="text-[11px] text-gray-600 mt-6 leading-relaxed">
        Rows whose role list is <code className="text-gray-500">ALL</code> ({ALL.length} roles) are universally accessible.
        Server-side <code className="text-gray-500">requireRole(...)</code> middleware on each backend route is the real
        authorisation boundary; this page reflects the matching client-side gate (sidebar visibility + route guard).
      </p>
    </div>
  )
}
